/* eslint-disable react-hooks/exhaustive-deps */
import { Outlet, Link } from "react-router-dom";
import { ProSidebar, Menu, MenuItem, SidebarContent } from "react-pro-sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faUser,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import Logo from "../assets/white-icon-large.png";
import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Footer from "./Footer";
import SidebarToggle, { DefaultSidebarBreakpoint } from "./SidebarToggle";
import HeaderSm from "./HeaderSm";
import { useAppSelector } from "../app/hooks";
import { selectThoughtSpotAppEmbedContext } from "../features/console/consoleSlice";
import {
  getAutomaticReloadFlag,
  getDisableAutoReloadTurnedOffNotif,
  resetTimerThoughtSpotTypes,
  setAutomaticReloadFlag,
  setDisableAutoReloadTurnedOffNotif,
  turnOffTimerThoughtSpotTypes,
} from "..";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

//http://webcache.googleusercontent.com/search?q=cache:https%3A%2F%2Fwww.npmjs.com%2Fpackage%2Freact-pro-sidebar
//for sidebar API instructions

interface LayoutProps {
  sidebarMenu?: React.ReactNode;
  navMenu?: React.ReactNode;
  breakPoint?: "xxl" | "xl" | "lg" | "md" | "sm" | "xs";
  smallHeader?: boolean;
  footer?: React.ReactNode;
  contactUsPath?: string;
  signOutPath?: string;
  customLogo?: React.ReactNode;
  headerCustomCSS?: string;
}

/**
 * Layout component for the main site.
 * @param props
 * @returns Layout component.
 */
const Layout = (props: LayoutProps) => {
  const page = useLocation().pathname;
  const searchParams = useLocation().search;
  const [showUpcomingMaintenanceMessage, setShowUpcomingMaintenanceMessage] =
    useState<string | null>(null);
  const [systemOfflineMessage, setSystemOfflineMessage] = useState<
    string | null
  >(null);
  const [systemOfflineFlag, setSystemOfflineFlag] = useState<boolean>(false);
  const [isShown, setIsShown] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [autoReloadToggle, setAutoReloadToggle] = useState(
    getAutomaticReloadFlag(),
  );
  const [outletKey, setOutletKey] = useState<number>(1);
  const [turnOffReloadModalVisibility, setTurnOffReloadModalVisibility] =
    useState(false);

  const thoughtSpotAppEmbedContext = useAppSelector(
    selectThoughtSpotAppEmbedContext,
  );

  React.useEffect(() => {
    setAutomaticReloadFlag(autoReloadToggle);
  }, [autoReloadToggle]);

  React.useEffect(() => {
    if (
      thoughtSpotAppEmbedContext &&
      thoughtSpotAppEmbedContext.data.type &&
      autoReloadToggle
    ) {
      /* 
        Events:
        Open liveboard:
        {
            "type": "data",
            "data": {...},
            "status": "end"
        }

        Explore viz:
        {
            "type": "explore",
            "data": {...},
            "status": "end"
        }

        Close dialog:
        {
            "type": "dialog-close",
            "data": {...},
            "status": "end"
        }

        Edit:
        {
            "type": "edit",
            "data": {...},
            "status": "end"
        }

        Open dialog:
        {
            "type": "dialog-open",
            "data": {...},
            "status": "end"
        }
      */

      if (
        resetTimerThoughtSpotTypes.includes(
          thoughtSpotAppEmbedContext.data.type,
        )
      ) {
        resetTimer();
      }
      if (
        turnOffTimerThoughtSpotTypes.includes(
          thoughtSpotAppEmbedContext.data.type,
        )
      ) {
        setAutoReloadToggle(false);
        //Show modal if it wasn't already disabled
        if (!getDisableAutoReloadTurnedOffNotif()) {
          setTurnOffReloadModalVisibility(true);
        }
      }
    }
  }, [thoughtSpotAppEmbedContext]);

  const toggleModal = () => {
    setTurnOffReloadModalVisibility(!turnOffReloadModalVisibility);
  };

  let autoReloadTimerRef = useRef<number>();

  const launchDelayedReload = (reason: string) => {
    clearTimeout(autoReloadTimerRef.current);
    autoReloadTimerRef.current = window.setTimeout(() => {
      console.log("AutoReload Refresh");
      setOutletKey(Math.random());
    }, 60000); //60000 = 60 seconds
    console.log(
      "AutoReload Timer ref (" + reason + "): " + autoReloadTimerRef.current,
    );
  };

  const resetTimer = () => {
    if (autoReloadToggle) {
      launchDelayedReload("Timer Reset");
    }
  };

  const handleToggle = (value: boolean) => {
    setToggled(value);
    if (!value) {
      setIsShown(false);
    }
  };

  const handleToggleClick = () => {
    setIsShown(true);
    setToggled(true);
  };

  const customHeaderCSS = props.headerCustomCSS
    ? " " + props.headerCustomCSS
    : "";

  React.useEffect(() => {
    if (autoReloadToggle) {
      launchDelayedReload("State Change");
    } else {
      //clears programmed refresh if the user toggles it off while one is running
      clearTimeout(autoReloadTimerRef.current);
    }
  }, [autoReloadToggle]);

  React.useEffect(() => {
    if (autoReloadToggle) {
      launchDelayedReload("component mount");
    }

    //document.body.addEventListener("mousemove", resetTimer);

    return () => {
      //cleanup
      clearTimeout(autoReloadTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (autoReloadToggle) {
      launchDelayedReload("Subsequent Refresh");
    }
  }, [outletKey]);

  if (!autoReloadToggle) {
    clearTimeout(autoReloadTimerRef.current);
  }

  return (
    <div className={"master " + (props.smallHeader ? "sm" : "")}>
      {props.smallHeader ? (
        <HeaderSm
          contactUsPath={props.contactUsPath}
          signOutPath={props.signOutPath}
          customLogo={props.customLogo}
          headerCustomCSS={props.headerCustomCSS}
          onAutoReloadToggleChange={(toggle) => setAutoReloadToggle(toggle)}
          autoReloadToggleValue={autoReloadToggle}
        />
      ) : (
        <div className={"banner sm" + customHeaderCSS}>
          {props.customLogo ?? (
            <div className="banner-text">
              <Link className="title" to="/">
                GALLUS INSIGHTS
              </Link>
            </div>
          )}
          <div className="banner-image">
            <div className="d-flex align-items-center">
              {props.navMenu}
              <Link className="title" to="/">
                <img className="logo" src={Logo} alt="Gallus Insights" />
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="wrapper">
        {/* <div className='contains-sidebar'> */}
        {props.breakPoint ? (
          <SidebarToggle
            breakpoint={DefaultSidebarBreakpoint}
            onClick={() => handleToggleClick()}
          />
        ) : (
          <></>
        )}
        <ProSidebar
          collapsed={!isShown && page !== "/"} //so sidebar won't be collapsed in the initial page, and only then
          breakPoint={props.breakPoint}
          onMouseEnter={() => setIsShown(true)}
          onMouseLeave={() => setIsShown(false)}
          toggled={props.breakPoint ? toggled : false}
          onToggle={handleToggle}
        >
          <SidebarContent>
            <Menu iconShape="square">
              {props.sidebarMenu ? (
                props.sidebarMenu
              ) : (
                <>
                  {/* <MenuItem>
                    <Link to="signUp">
                      <div className="sidebar-item">
                        <FontAwesomeIcon className="me-2" icon={faUserPlus} />
                        Sign Up
                      </div>
                    </Link>
                  </MenuItem> */}
                  <MenuItem>
                    <Link to="login">
                      <div className="sidebar-item">
                        <FontAwesomeIcon className="me-2" icon={faUser} />
                        Login
                      </div>
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link to="contactus">
                      <div className="sidebar-item">
                        <FontAwesomeIcon className="me-2" icon={faEnvelope} />
                        Contact Us
                      </div>
                    </Link>
                  </MenuItem>
                </>
              )}
            </Menu>
          </SidebarContent>
        </ProSidebar>
        <div
          className="system-offline-message"
          style={{ display: systemOfflineFlag ? "block" : "none" }}
        >
          {systemOfflineMessage}
        </div>
        <div
          className="maintenance-message"
          style={{ display: showUpcomingMaintenanceMessage ? "block" : "none" }}
        >
          {showUpcomingMaintenanceMessage}
        </div>
        <div
          className="contains-outlet"
          style={{ display: systemOfflineFlag ? "none" : "block" }}
        >
          <Outlet key={outletKey} />
        </div>
      </div>
      {props.footer ?? <Footer />}
      <Modal isOpen={turnOffReloadModalVisibility} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Auto Reload has been disabled
        </ModalHeader>
        <div>
          <ModalBody>
            Editing a dashboard or exploring a visualization will disable Auto
            Reload
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                setDisableAutoReloadTurnedOffNotif(true);
                toggleModal();
              }}
            >
              Disable this notification
            </Button>
            <Button color="secondary" onClick={() => toggleModal()}>
              Close
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
};

export default Layout;
